import React from 'react';
import {useNavigate} from 'react-router-dom';
import {Button, Spin, Typography} from 'antd';

import CarsAvailabilityView from '../deployments/CarsAvailabilityView';
import BookingSummary from './BookingSummary';
import ErrorBoundary from '../../../components/ErrorBoundary';

import {messageType, showMessages} from '../../../helpers/message';
import {useAppSelector} from '../../../hooks/useStore';
import {selectAccountMembershipNumber, selectIsStaff} from '../../../store/slices/userSlice';
import {
    useCreateBookingMutation,
    useGetDeploymentsByTimeQuery,
    useLazyGetBookingEstimatedCostQuery
} from '../../../store/api/bookingsApi';
import {BaseError} from '../../../types/apiTypes';

import './BookingGridView.less';
import {Deployment, NewBooking} from '../../../types/bookingTypes';

const BookingGridView = () => {
    const accountMembershipNum = useAppSelector(selectAccountMembershipNumber);
    const isStaff = useAppSelector(selectIsStaff);
    const membershipID = useAppSelector(state => state.user.membership)?.id;
    const navigate = useNavigate();

    const [deployments, setDeployments] = React.useState<Deployment[]>([]);
    const [newBooking, setNewBooking] = React.useState<NewBooking | undefined>(undefined);
    const [showBookingSummary, setShowBookingSummary] = React.useState<boolean>(false);
    const [timeCharge, setTimeCharge] = React.useState<number>(0);

    const {data: deploymentsData, isLoading: isDeploymentsLoading, error: deploymentsDataError} = useGetDeploymentsByTimeQuery({});
    const [getBookingEstimatedCost, {error: bookingEstimatedCostError, isFetching: isEstBookingCostLoading}] = useLazyGetBookingEstimatedCostQuery();
    const [createBooking, {data: createdBooking, isLoading: createBookingLoading, isSuccess, error: createBookingError}] = useCreateBookingMutation();

    React.useEffect(() => {
        if (isStaff && !accountMembershipNum) navigate('/member_change');

        if (deploymentsData && !isDeploymentsLoading) {
            setDeployments(deploymentsData)
        } else if (deploymentsDataError) {
            showMessages((deploymentsDataError as BaseError).data?.message, messageType.error);
        }
    }, [isStaff, accountMembershipNum, navigate, deploymentsData, deploymentsDataError, isDeploymentsLoading]);

    React.useEffect(() => {
        if (isSuccess && createdBooking) {
            showMessages('Booking has successfully been created!', messageType.success);

            navigate(`/booking/${createdBooking.id}`);
        } else if (createBookingError) {
            showMessages((createBookingError as BaseError).data?.message, messageType.error);
        }
    }, [isSuccess, createBookingError, navigate, createdBooking]);

    React.useEffect(() => {
        if (bookingEstimatedCostError) showMessages((bookingEstimatedCostError as BaseError).data?.message, messageType.error);
    }, [bookingEstimatedCostError]);

    const confirmBooking = async () => {
        if (newBooking) {
            await createBooking({...newBooking, membershipID});
        }
    };

    React.useEffect(() => {
        if (showBookingSummary && newBooking) {
            const getEstimatedCost = async () => {
                const response = await getBookingEstimatedCost({
                    startTime: newBooking.startedAt,
                    endTime: newBooking.endedAt,
                    membershipID,
                    deploymentID: newBooking.deploymentID
                }).unwrap();

                if (response) {
                    // Set the time cost amount if returned, else just set zero.
                    setTimeCharge(response.transformedCosts.timeCost);
                }
            };
            getEstimatedCost();
        }
    }, [getBookingEstimatedCost, showBookingSummary, newBooking, membershipID]);

    return (
        isDeploymentsLoading || isEstBookingCostLoading ? <Spin/> : (
            <ErrorBoundary>
                {deployments && deployments.length !== 0 ? (
                    <div className="booking-grid">
                        <div className="grid-button-group">
                            {!showBookingSummary &&
                                <Button
                                    danger
                                    size="large"
                                    disabled={!newBooking}
                                    onClick={() => setNewBooking(undefined)}
                                >
                                    Clear Booking
                                </Button>
                            }
                        </div>
                        <div className="calendar-group">
                            {showBookingSummary && newBooking ? (
                                <BookingSummary
                                    newBooking={newBooking}
                                    timeCharge={timeCharge}
                                    goToBookingSummary={() => setShowBookingSummary(!showBookingSummary)}
                                    confirmBooking={confirmBooking}
                                    isBookingLoading={createBookingLoading}
                                />
                            ) : (
                                <CarsAvailabilityView
                                    deployments={deployments}
                                    newBooking={newBooking}
                                    setNewBooking={setNewBooking}
                                    clearNewBooking={() => setNewBooking(undefined)}
                                    goToBookingSummary={() => setShowBookingSummary(!showBookingSummary)}
                                    itemsPerPage={10000}
                                />
                            )}
                        </div>
                    </div>
                ) : (
                    // Only display page title when there is no deployment.
                    <Typography.Title level={3}>Create Booking</Typography.Title>
                )}
            </ErrorBoundary>
        )
    );
};

export default BookingGridView;
